import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {Document} from '../models/documents/document.model';
import {Documents} from '../actions/document.actions';
import {DocumentService} from '../services/document.service';
import ClearState = Auth.ClearState;
import GetDocuments = Documents.GetDocuments;
import GetDocument = Documents.GetDocument;
import CreateDocument = Documents.CreateDocument;
import UpdateDocument = Documents.UpdateDocument;
import DeleteDocument = Documents.DeleteDocument;
import DeleteDocuments = Documents.DeleteDocuments;
import DownloadDocumentsZip = Documents.DownloadDocumentsZip;

export interface DocumentStateModel {
  documents: Document[];
  id: string | null;
  document: {
    id: string,
    url: string
  } | null;
  zip: string[];
}

@State<DocumentStateModel>({
  name: 'documents',
  defaults: {
    documents: [],
    id: null,
    document: null,
    zip: []
  }
})
@Injectable()
export class DocumentState {
  constructor(private documentService: DocumentService
  ) {
  }

  @Selector()
  static documents(state: DocumentStateModel): Array<Document> {
    return state.documents;
  }

  @Selector()
  static document(state: DocumentStateModel): string {
    return state.document?.url;
  }

  @Selector()
  static zip(state: DocumentStateModel): string[] {
    return state.zip;
  }


  @Action(GetDocuments)
  getDocuments(ctx: StateContext<DocumentStateModel>, action: GetDocuments): Observable<Document[]> {
    if (!action.force) {
      const documents = ctx.getState().documents;
      if (documents.length > 0 && ctx.getState().id === action.id) {
        return of(documents);
      }
    }
    return this.documentService.getDocuments(action.id).pipe(
      tap(jobDocuments => {
        ctx.patchState({
          documents: jobDocuments.documents,
          id: jobDocuments.id
        });
      }),
      map(jobDocuments => jobDocuments.documents),
    );
  }

  @Action(GetDocument)
  getDocument(ctx: StateContext<DocumentStateModel>, action: GetDocument): Observable<string> {
    if (!action.force) {
      const document = ctx.getState().document;
      if (document && document.id === action.id) {
        return of(document.url);
      }
    }
    return this.documentService.getDocument(action.jobId, action.id).pipe(
      tap(url => {
        ctx.patchState({
          document: {
            url,
            id: action.id
          }
        });
      })
    );
  }

  @Action(CreateDocument)
  createDocument(ctx: StateContext<DocumentStateModel>, action: CreateDocument): Observable<Document[]> {
    return this.documentService.createDocument(action.jobId, action.request, action.file).pipe(
      tap(jobDocuments => {
        ctx.patchState({
          documents: jobDocuments.documents
        });
      }),
      map(jobDocuments => jobDocuments.documents)
    );
  }

  @Action(UpdateDocument)
  updateDocument(ctx: StateContext<DocumentStateModel>, action: UpdateDocument): Observable<Document[]> {
    return this.documentService.updateDocument(action.jobId, action.id, action.request).pipe(
      map(jobDocuments => jobDocuments.documents),
      tap(documents => {
        ctx.patchState({
          documents
        });
      }),
    );
  }

  @Action(DeleteDocument)
  deleteDocument(ctx: StateContext<DocumentStateModel>, action: DeleteDocument): Observable<Document[]> {
    return this.documentService.deleteDocument(action.jobId, action.id).pipe(
      map(jobDocuments => jobDocuments.documents),
      tap(documents => {
        ctx.patchState({
          documents
        });
      }),
    );
  }

  @Action(DeleteDocuments)
  deleteDocuments(ctx: StateContext<DocumentStateModel>, action: DeleteDocuments): Observable<Document[]> {
    return this.documentService.deleteDocuments(action.jobId, action.documents).pipe(
      map(jobDocuments => jobDocuments.documents),
      tap(documents => {
        ctx.patchState({
          documents
        });
      }),
    );
  }

  @Action(DownloadDocumentsZip)
  downloadDocumentsZip(ctx: StateContext<DocumentStateModel>, action: DownloadDocumentsZip): Observable<string[]> {
    return this.documentService.downloadDocumentsZip(action.jobId, action.documents).pipe(
      tap((zip: string[]) => {
        ctx.patchState({
          zip
        });
      }),
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<DocumentStateModel>): void {
    ctx.setState({
      documents: [],
      id: null,
      document: null,
      zip: []
    });
  }
}
